/**
 * HealthDetailModal Component
 *
 * Displays detailed health information for a tenant based on isolation level:
 * - Row-level: Always ready, RLS-based
 * - Schema: Shows schema status and provisioning option
 * - Container: Shows container stack status and actions
 *
 * @module features/tenants/components/modals/HealthDetailModal
 */

import { useState, useEffect, useCallback } from 'react';
import { CloseIcon, RefreshIcon } from '../../../../components/common/Icons';
import type { Tenant, ContainerStatus, ContainerProvisioningProgress, ContainerTransition } from '../../types';
import { getHealthStatus } from '../../utils';
import { fetchContainerLogs } from '../../api/containerApi';

interface HealthDetailModalProps {
  /** The tenant to show health details for (null = closed) */
  tenant: Tenant | null;
  /** Callback to close the modal */
  onClose: () => void;
  /** Container statuses by tenant ID */
  containerStatuses: Record<string, ContainerStatus>;
  /** Container transitions by tenant ID */
  containerTransitioning: Record<string, ContainerTransition>;
  /** Tenant ID currently provisioning schema */
  isProvisioningSchema: string | null;
  /** Callback to provision schema */
  onProvisionSchema: (tenantId: string) => void;
  /** Tenant ID currently provisioning container */
  isProvisioningContainer: string | null;
  /** Container provisioning progress by tenant ID */
  containerProvisioningProgress: Record<string, ContainerProvisioningProgress>;
  /** Callback to provision containers */
  onProvisionContainer: (tenantId: string) => void;
  /** Tenant ID with container action in progress */
  containerActionInProgress: string | null;
  /** Callback for container actions */
  onContainerAction: (tenantId: string, action: 'start' | 'stop' | 'restart') => void;
  /** Authenticated fetch function for API calls */
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

/**
 * Renders health details modal for a tenant.
 */
export const HealthDetailModal: React.FC<HealthDetailModalProps> = ({
  tenant,
  onClose,
  containerStatuses,
  containerTransitioning,
  isProvisioningSchema,
  onProvisionSchema,
  isProvisioningContainer,
  containerProvisioningProgress,
  onProvisionContainer,
  containerActionInProgress,
  onContainerAction,
  authFetch,
}) => {
  const [activeTab, setActiveTab] = useState<'status' | 'logs'>('status');
  const [logService, setLogService] = useState<'api' | 'postgres' | 'redis'>('api');
  const [logLines, setLogLines] = useState(100);
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    if (!tenant || tenant.isolation_level !== 'container') return;
    // Don't try to fetch logs if container isn't provisioned
    const containerStatus = containerStatuses[tenant.id];
    if (!containerStatus || containerStatus.status === 'not_provisioned') {
      setLogsError(null);
      setLogs('');
      return;
    }
    setLogsLoading(true);
    setLogsError(null);
    try {
      const response = await fetchContainerLogs(authFetch, tenant.id, logService, logLines);
      setLogs(response.logs);
    } catch (err) {
      setLogsError(err instanceof Error ? err.message : 'Failed to fetch logs');
      setLogs('');
    } finally {
      setLogsLoading(false);
    }
  }, [authFetch, tenant?.id, logService, logLines, containerStatuses]);

  // Reset tab when modal opens for a different tenant
  useEffect(() => {
    setActiveTab('status');
    setLogs('');
    setLogsError(null);
  }, [tenant?.id]);

  // Load logs when switching to logs tab
  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab, logService, loadLogs]);

  if (!tenant) {
    return null;
  }

  const handleProvisionSchema = () => {
    onClose();
    onProvisionSchema(tenant.id);
  };

  const handleProvisionContainer = async () => {
    // Don't close modal - stay open to show provisioning progress
    try {
      await onProvisionContainer(tenant.id);
    } catch {
      // Error is already handled in the hook (sets progress to failed state)
      // Just catch to prevent unhandled promise rejection
    }
  };

  const handleContainerAction = (action: 'start' | 'stop' | 'restart') => {
    onClose();
    onContainerAction(tenant.id, action);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: tenant.isolation_level === 'container' ? '700px' : '500px' }}>
        <div className="modal-header">
          <h2>Health Details: {tenant.name}</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="modal-body">
          {/* Row-Level Isolation */}
          {tenant.isolation_level === 'row' && (
            <div className="health-detail-section">
              <div className="health-status-large">
                <span className="badge badge-success" style={{ fontSize: '1.1em', padding: '8px 16px' }}>Ready</span>
              </div>
              <div className="info-grid" style={{ marginTop: '20px' }}>
                <div className="info-item">
                  <span className="info-label">Isolation Type</span>
                  <span className="info-value">Row-Level Security (RLS)</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Database</span>
                  <span className="info-value">Shared (public schema)</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Data Protection</span>
                  <span className="info-value">PostgreSQL RLS policies filter all queries by tenant_id</span>
                </div>
              </div>
              <p className="text-secondary" style={{ marginTop: '16px', fontSize: '0.9em' }}>
                Row-level tenants are always available. Data isolation is enforced at the database level.
              </p>
            </div>
          )}

          {/* Schema Isolation */}
          {tenant.isolation_level === 'schema' && (
            <div className="health-detail-section">
              <div className="health-status-large">
                {tenant.schema_name ? (
                  <span className="badge badge-success" style={{ fontSize: '1.1em', padding: '8px 16px' }}>Provisioned</span>
                ) : (
                  <span className="badge badge-warning" style={{ fontSize: '1.1em', padding: '8px 16px' }}>Not Provisioned</span>
                )}
              </div>
              <div className="info-grid" style={{ marginTop: '20px' }}>
                <div className="info-item">
                  <span className="info-label">Isolation Type</span>
                  <span className="info-value">Dedicated PostgreSQL Schema</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Schema Name</span>
                  <span className="info-value" style={{ fontFamily: 'monospace' }}>
                    {tenant.schema_name || `tenant_${tenant.slug}`}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className="info-value">
                    {tenant.schema_name ? 'Schema active and operational' : 'Awaiting schema provisioning'}
                  </span>
                </div>
              </div>
              {!tenant.schema_name && (
                <div style={{ marginTop: '20px' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleProvisionSchema}
                    disabled={isProvisioningSchema === tenant.id}
                  >
                    {isProvisioningSchema === tenant.id ? (
                      <><RefreshIcon size={14} className="spin" /> Provisioning...</>
                    ) : (
                      'Provision Schema'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Container Isolation */}
          {tenant.isolation_level === 'container' && (
            <div className="health-detail-section">
              {/* Tab Navigation */}
              <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--border-color)', marginBottom: '16px' }}>
                <button
                  onClick={() => setActiveTab('status')}
                  style={{
                    padding: '8px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === 'status' ? '2px solid var(--primary)' : '2px solid transparent',
                    color: activeTab === 'status' ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'status' ? 600 : 400,
                    fontSize: '0.9em',
                  }}
                >
                  Status
                </button>
                <button
                  onClick={() => setActiveTab('logs')}
                  style={{
                    padding: '8px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === 'logs' ? '2px solid var(--primary)' : '2px solid transparent',
                    color: activeTab === 'logs' ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'logs' ? 600 : 400,
                    fontSize: '0.9em',
                  }}
                >
                  Logs
                </button>
              </div>

              {/* Status Tab */}
              {activeTab === 'status' && (() => {
                const status = containerStatuses[tenant.id];
                const health = getHealthStatus(tenant, containerStatuses, containerTransitioning);
                return (
                  <>
                    <div className="health-status-large">
                      <span className={`badge badge-${health.color}`} style={{ fontSize: '1.1em', padding: '8px 16px' }}>
                        {health.label}
                      </span>
                    </div>

                    <div className="info-grid" style={{ marginTop: '20px' }}>
                      <div className="info-item">
                        <span className="info-label">Isolation Type</span>
                        <span className="info-value">Dedicated Container Stack</span>
                      </div>
                      {status?.access_url && (
                        <div className="info-item">
                          <span className="info-label">Access URL</span>
                          <a href={status.access_url} target="_blank" rel="noopener noreferrer" className="tenant-url-link">
                            {status.access_url}
                          </a>
                        </div>
                      )}
                    </div>

                    {status?.containers && Object.keys(status.containers).length > 0 && (() => {
                      // Hide container stack while provisioning is actively in progress —
                      // containers are still starting up and would show misleading states.
                      const provProgress = containerProvisioningProgress[tenant.id];
                      const isActivelyProvisioning = provProgress && !['completed', 'failed'].includes(provProgress.status);
                      if (isActivelyProvisioning) return null;
                      return (
                        <div style={{ marginTop: '20px' }}>
                          <h4 style={{ marginBottom: '12px', fontSize: '0.95em', color: 'var(--text-secondary)' }}>
                            Container Stack
                          </h4>
                          <div className="container-stack-list">
                            {Object.entries(status.containers).map(([name, info]) => (
                              <div key={name} className="container-stack-item">
                                <span className={`status-dot ${info.running && info.healthy ? 'status-dot-success' : info.running ? 'status-dot-warning' : 'status-dot-error'}`}></span>
                                <span className="container-name">{name}</span>
                                <span className="container-status">
                                  {info.running ? (info.healthy ? 'Healthy' : 'Unhealthy') : 'Stopped'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {status?.error && (() => {
                      // Hide error box during active provisioning — transient errors are expected
                      const provProgress = containerProvisioningProgress[tenant.id];
                      const isActivelyProvisioning = provProgress && !['completed', 'failed'].includes(provProgress.status);
                      if (isActivelyProvisioning) return null;
                      return (
                        <div className="error-box" style={{ marginTop: '16px' }}>
                          <strong>Error:</strong> {status.error}
                        </div>
                      );
                    })()}

                    {/* Provisioning Progress Display */}
                    {containerProvisioningProgress[tenant.id] && (() => {
                      const progress = containerProvisioningProgress[tenant.id];
                      const isActive = !['completed', 'failed'].includes(progress.status);
                      // Don't show completed status in progress section (it's shown in health badge)
                      if (progress.status === 'completed') return null;
                      // Don't show stale "failed" progress when containers are already running.
                      // This prevents the modal from showing "Provisioning Failed" for tenants
                      // whose containers are actually healthy (Bug 2 inconsistency fix).
                      if (progress.status === 'failed' && status?.status === 'running') return null;
                      return (
                        <div style={{
                          marginTop: '20px',
                          padding: '16px',
                          background: progress.status === 'failed' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(124, 58, 237, 0.1)',
                          borderRadius: '8px',
                          border: `1px solid ${progress.status === 'failed' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(124, 58, 237, 0.3)'}`,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            {isActive && <RefreshIcon size={16} className="spin" style={{ color: 'var(--primary)' }} />}
                            <span style={{ fontWeight: 600, fontSize: '0.95em' }}>
                              {progress.status === 'failed' ? 'Provisioning Failed' : 'Provisioning in Progress'}
                            </span>
                          </div>
                          <div style={{
                            height: '8px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            marginBottom: '8px',
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${progress.progress_pct}%`,
                              background: progress.status === 'failed' ? 'var(--error)' : 'var(--primary)',
                              transition: 'width 0.3s ease',
                            }} />
                          </div>
                          <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                            Step {progress.current_step}/{progress.total_steps}: {progress.message}
                          </div>
                          {progress.status === 'failed' && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleProvisionContainer}
                              style={{ marginTop: '12px' }}
                            >
                              <RefreshIcon size={14} /> Retry Provisioning
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {(!status || status.status === 'not_provisioned') && !isProvisioningContainer && !containerProvisioningProgress[tenant.id] && (
                        <button
                          className="btn btn-primary"
                          onClick={handleProvisionContainer}
                        >
                          Provision Containers
                        </button>
                      )}
                      {(!status || status.status === 'not_provisioned') && isProvisioningContainer === tenant.id && (
                        <button
                          className="btn btn-primary"
                          disabled
                        >
                          <RefreshIcon size={14} className="spin" /> Starting...
                        </button>
                      )}
                      {status?.status === 'stopped' && (
                        <button
                          className="btn btn-success"
                          onClick={() => handleContainerAction('start')}
                          disabled={containerActionInProgress === tenant.id}
                        >
                          Start Containers
                        </button>
                      )}
                      {status?.status === 'running' && (
                        <>
                          <button
                            className="btn btn-warning"
                            onClick={() => handleContainerAction('restart')}
                            disabled={containerActionInProgress === tenant.id}
                          >
                            Restart
                          </button>
                          <button
                            className="btn btn-ghost"
                            onClick={() => handleContainerAction('stop')}
                            disabled={containerActionInProgress === tenant.id}
                          >
                            Stop
                          </button>
                        </>
                      )}
                      {(status?.status === 'error' || status?.status === 'unhealthy') && (
                        <button
                          className="btn btn-warning"
                          onClick={() => handleContainerAction('restart')}
                          disabled={containerActionInProgress === tenant.id}
                        >
                          Restart Containers
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Logs Tab */}
              {activeTab === 'logs' && (
                <>
                  {/* Show info message if container not provisioned */}
                  {(!containerStatuses[tenant.id] || containerStatuses[tenant.id]?.status === 'not_provisioned') && (
                    <div style={{
                      padding: '20px',
                      background: 'rgba(124, 58, 237, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(124, 58, 237, 0.3)',
                      textAlign: 'center',
                    }}>
                      <p style={{ marginBottom: '8px', fontWeight: 500 }}>Container Not Provisioned</p>
                      <p style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                        Switch to the Status tab and provision containers first to view logs.
                      </p>
                    </div>
                  )}
                  {containerStatuses[tenant.id] && containerStatuses[tenant.id]?.status !== 'not_provisioned' && (<>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Service
                      </label>
                      <select
                        value={logService}
                        onChange={(e) => setLogService(e.target.value as 'api' | 'postgres' | 'redis')}
                        className="form-control"
                        style={{ minWidth: '120px' }}
                      >
                        <option value="api">API</option>
                        <option value="postgres">PostgreSQL</option>
                        <option value="redis">Redis</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Lines
                      </label>
                      <select
                        value={logLines}
                        onChange={(e) => setLogLines(Number(e.target.value))}
                        className="form-control"
                        style={{ minWidth: '80px' }}
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                      </select>
                    </div>
                    <button
                      className="btn btn-ghost"
                      onClick={loadLogs}
                      disabled={logsLoading}
                      style={{ padding: '6px 12px' }}
                    >
                      <RefreshIcon size={14} className={logsLoading ? 'spin' : ''} />
                      {' '}Refresh
                    </button>
                  </div>

                  {logsError && (
                    <div className="error-box" style={{ marginBottom: '12px' }}>
                      {logsError}
                      {logsError.toLowerCase().includes('not provisioned') && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', opacity: 0.8 }}>
                          Use the Status tab to provision containers first, then return here to view logs.
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      padding: '6px 12px',
                      borderBottom: '1px solid var(--border-color)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.8em',
                      color: 'var(--text-secondary)',
                    }}>
                      <span>{tenant.slug} / {logService}</span>
                      <span>Last {logLines} lines</span>
                    </div>
                    <pre style={{
                      padding: '12px',
                      margin: 0,
                      fontSize: '0.8em',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: '400px',
                      overflow: 'auto',
                      color: 'var(--text-primary)',
                    }}>
                      {logsLoading ? 'Loading logs...' : logs || 'No logs available'}
                    </pre>
                  </div>
                  </>
                )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
